import React, { useState, useEffect, useRef } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { Plus, Trash2, User, Pencil, Save, X, Image, Lock, CreditCard, History, ChevronDown, ChevronUp, Mail, Search, Filter, Wifi, CheckCircle, AlertCircle, Code, Eye, MessageSquare, RotateCcw, Archive, RefreshCw } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';
import { useSeason } from '../../context/SeasonContext';
import { isValidSeason, seasonOptions } from '../../utils/season';
import { API_URL, jsonAuthHeaders, readJsonOrThrow } from '../../utils/api';
import {
  DEFAULT_MESSAGES, DEFAULT_PRICE_LIST_LINK, TEMPLATE_PLACEHOLDERS, buildOfferMessage, formatStay, offerMoneyText
} from '../../utils/offerShare';
import LoadingSpinner from '../../components/LoadingSpinner';
import EventSettings from '../events/EventSettings';

// Ayarlar > Mesaj Şablonları (settings kategorisi 'messages'). Boş alan = kod varsayılanı (DEFAULT_MESSAGES).
const MESSAGE_FIELDS = [
  { key: 'waDetailed', label: 'WhatsApp — detaylı mesaj', hint: 'Oda, tarih, tutar ve PDF linkiyle tam mesaj.', rows: 10 },
  { key: 'waLink', label: 'WhatsApp — yalnız link', hint: 'Kısa mesaj: yalnızca teklif PDF linki.', rows: 6 },
  { key: 'instagram', label: 'Instagram mesajı', hint: 'Instagram DM ile gönderilen mesaj.', rows: 10 },
  { key: 'priceList', label: 'Fiyat listesi mesajı', hint: 'Teklif yerine fiyat listesi linki gönderilirken kullanılır.', rows: 5 },
  { key: 'signature', label: 'İmza / iletişim satırları', hint: 'Mesajlardaki {signature} yerine gelir.', rows: 4 },
];
const PREVIEW_KINDS = [
  { key: 'waDetailed', label: 'WhatsApp detaylı' },
  { key: 'waLink', label: 'WhatsApp link' },
  { key: 'instagram', label: 'Instagram' },
  { key: 'priceList', label: 'Fiyat listesi' },
];
const MESSAGE_KEYS = [...MESSAGE_FIELDS.map(f => f.key), 'priceListLink'];
// Önizleme için sabit örnek teklif
const SAMPLE_OFFER = {
  id: 850, customerName: 'Ayşe Yılmaz', checkIn: '2026-12-25', checkOut: '2026-12-28', nights: 3,
  totalPrice: 85000, currency: 'TL', rooms: [{ name: 'Deluxe Oda' }]
};
const normalizeText = (value) => String(value ?? '').replace(/\r\n/g, '\n').trim();
const defaultMessage = (key) => DEFAULT_MESSAGES[key] || '';
// Ekranda: kayıtlı metin, yoksa varsayılan
const messageFormFrom = (saved) => MESSAGE_KEYS.reduce((acc, key) => {
  const v = saved?.[key];
  acc[key] = typeof v === 'string' && v.trim() ? v : defaultMessage(key);
  return acc;
}, {});
// Kayıtta: varsayılanla aynı (ya da boş) metin '' saklanır → kod varsayılanı ileride güncellenebilir
const messagePayloadFrom = (form) => MESSAGE_KEYS.reduce((acc, key) => {
  const v = normalizeText(form[key]);
  if (!v || v === normalizeText(defaultMessage(key))) acc[key] = '';
  else acc[key] = key === 'priceListLink' ? v : String(form[key]).replace(/\r\n/g, '\n');
  return acc;
}, {});

// Tarih/saat gösterimi ('—' = yok/geçersiz)
const formatDateOnly = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('tr-TR');
};
const formatDateTime = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' });
};

export default function Settings() {
  const { currentUser } = useAuth();
  const { data, fetchData, refreshData, loading: globalLoading, incrementReads, incrementWrites } = useData();
  
  const [newSource, setNewSource] = useState('');

  // Tab State
  const [activeTab, setActiveTab] = useState('sources');
  // Aktif sezon ayarı
  const { activeSeason, activeSeasonSaved, refreshActiveSeason } = useSeason();
  const [seasonForm, setSeasonForm] = useState('');
  const [seasonSaving, setSeasonSaving] = useState(false);
  const seasonChoices = seasonOptions(data.offers || [], activeSeason);
  // Seçim kutusu kayıtlı aktif sezonla açılır (kullanıcı değiştirene kadar)
  useEffect(() => { setSeasonForm(prev => (prev ? prev : activeSeason)); }, [activeSeason]);

  // Logo State
  const [logoSettings, setLogoSettings] = useState({ 
    light: '', 
    dark: '', 
    favicon: '', 
    pageTitle: '', 
    pdfBackground: '', 
    loginImage: '', 
    roomsLink: '', 
    showStats: true,
    hotelName: '',
    hotelPhone: '',
    hotelEmail: '',
    hotelConcept: ''
  });

  // Banka Ayarları State
  const [bankSettings, setBankSettings] = useState({
    bankName: '',
    accountHolder: '',
    tlIban: '',
    euroIban: '',
    usdIban: '',
    cancellationPolicy: '',
    emailSubject: '',
    emailBody: '',
    mailOrderInfo: ''
  });

  // Ayar kayıtları gerçekten okunmadan kaydetmeye izin verilmez (aksi halde boş form kayıtlı ayarların üzerine yazar)
  const [logosLoaded, setLogosLoaded] = useState(false);
  const [bankLoaded, setBankLoaded] = useState(false);
  const [bankError, setBankError] = useState('');

  // Mesaj Şablonları State (settings 'messages')
  const [messageForm, setMessageForm] = useState(() => messageFormFrom({}));
  const [messageSaved, setMessageSaved] = useState({}); // sunucudaki ham kayıt (bilinmeyen anahtarlar korunur)
  const [messagesStatus, setMessagesStatus] = useState('idle'); // idle, loading, ready, error
  const [messagesError, setMessagesError] = useState('');
  const [messagesSaving, setMessagesSaving] = useState(false);
  const [activeMessageField, setActiveMessageField] = useState('waDetailed');
  const [previewKind, setPreviewKind] = useState('waDetailed');
  const messageInputRefs = useRef({});

  // Silinenler State (yumuşak silinmiş teklifler)
  const [deletedOffers, setDeletedOffers] = useState([]);
  const [deletedLoading, setDeletedLoading] = useState(false);
  const [deletedError, setDeletedError] = useState('');
  const [deletedBusyId, setDeletedBusyId] = useState(null);

  // Şifre Değiştirme State
  const [passwordData, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordSaving, setPasswordSaving] = useState(false);

  // Kullanıcılar State
  const [editingUserId, setEditingUserId] = useState(null);
  const [userFormData, setUserFormData] = useState({
    name: '',
    email: '',
    password: '',
    permissions: {
      read: true,
      write: false,
      delete: false,
      view: true,
      view_reports: false,
      view_financials: false,
      manage_users: false
    }
  });

  // Loglar State
  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [expandedLog, setExpandedLog] = useState(null);
  const [logStartDate, setLogStartDate] = useState('');
  const [logEndDate, setLogEndDate] = useState('');
  const [logSearchUser, setLogSearchUser] = useState('');

  // Kullanıcılar için yerel state (Context yerine buradan yöneteceğiz çünkü auth header lazım)
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Bağlantı Testi State
  const [connectionStatus, setConnectionStatus] = useState('idle'); // idle, checking, success, error

  // HTML Kaynak Görünümü State
  const [showHtmlSource, setShowHtmlSource] = useState(false);

  const canWrite = currentUser?.permissions?.write;
  const canDelete = currentUser?.permissions?.delete;
  const canSeeMoney = !!currentUser?.permissions?.view_financials;
  const SUPER_ADMIN_EMAIL = 'abdullah@amcfikirsanat.com';
  const isAdmin = currentUser?.email === SUPER_ADMIN_EMAIL || currentUser?.permissions?.manage_users;

  // Auth Header Yardımcısı (token yoksa başlığı AuthContext'teki genel fetch sarmalayıcısı ekler)
  const getAuthHeaders = () => jsonAuthHeaders(currentUser?.token);

  // İşlem logu (bilgi amaçlı; performedBy'ı sunucu token'dan yazar). Log hatası asıl işlemi bozmasın.
  const writeLog = async (action, description) => {
    try {
      await fetch(`${API_URL}/activityLogs`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          action,
          description,
          performedBy: { uid: currentUser?.uid, email: currentUser?.email }
        })
      });
    } catch (error) {
      console.error("Log yazılamadı:", error);
    }
  };

  const checkConnection = async () => {
    setConnectionStatus('checking');
    try {
        await fetch(`${API_URL}/offerSources`);
        setConnectionStatus('success');
        setTimeout(() => setConnectionStatus('idle'), 3000);
    } catch (error) {
        console.error("Bağlantı hatası:", error);
        setConnectionStatus('error');
        setTimeout(() => setConnectionStatus('idle'), 3000);
    }
  };

  useEffect(() => {
    if (activeTab === 'season') fetchData('offers'); // sezon listesi tekliflerden çıkarılır
    if (activeTab === 'sources') fetchData('offerSources');
    if (activeTab === 'users') fetchUsers(); // fetchData yerine yerel fetchUsers kullanıyoruz
    if (activeTab === 'branding') fetchLogos();
    if (activeTab === 'bank' || activeTab === 'email') fetchBankSettings();
    if (activeTab === 'logs') fetchLogs();
    if (activeTab === 'messages') {
      fetchMessages();
      fetchLogos(); // önizleme: otel telefonu ve odalar linki (fiyat listesi linki boşsa)
    }
    if (activeTab === 'deleted' && canWrite) fetchDeletedOffers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const sources = data.offerSources || [];
  const loadingSources = globalLoading.offerSources;

  // fetchLogos ve fetchBankSettings tekil dökümanlar olduğu için burada kalabilir veya onlar da context'e taşınabilir.
  // Şimdilik sadece koleksiyonları taşıdık.

  const fetchLogos = async () => {
    try {
        const response = await fetch(`${API_URL}/settings/logos`);
        if (response.ok) {
            const data = await response.json();
            incrementReads(1);
            setLogosLoaded(true);
            setLogoSettings({
                light: '',
                dark: '',
                favicon: '', 
                pageTitle: '', 
                pdfBackground: '', 
                loginImage: '', 
                roomsLink: '', 
                showStats: true, 
                hotelName: '',
                hotelPhone: '',
                hotelEmail: '',
                hotelConcept: '',
                ...data
            });
        }
    } catch (error) {
        console.error("Logo fetch error", error);
    }
  };

  const fetchBankSettings = async () => {
    try {
        const response = await fetch(`${API_URL}/settings/bank`, { headers: getAuthHeaders() });
        const data = await readJsonOrThrow(response, 'Banka ayarları alınamadı.');
        incrementReads(1);
        setBankSettings(prev => ({ ...prev, ...(data && typeof data === 'object' ? data : {}) }));
        setBankLoaded(true);
        setBankError('');
    } catch (error) {
        console.error("Bank settings fetch error", error);
        setBankError(error.message);
    }
  };

  // Mesaj şablonları: boş alan ekranda varsayılan metinle görünür
  const fetchMessages = async () => {
    setMessagesStatus('loading');
    setMessagesError('');
    try {
        const response = await fetch(`${API_URL}/settings/messages`, { headers: getAuthHeaders() });
        const data = await readJsonOrThrow(response, 'Mesaj şablonları alınamadı.');
        const saved = data && typeof data === 'object' && !Array.isArray(data) ? data : {};
        incrementReads(1);
        setMessageSaved(saved);
        setMessageForm(messageFormFrom(saved));
        setMessagesStatus('ready');
    } catch (error) {
        console.error("Message settings fetch error", error);
        setMessagesError(error.message);
        setMessagesStatus('error');
    }
  };

  // Silinen teklifler (sunucu silinme tarihine göre yeniden eskiye sıralar)
  const fetchDeletedOffers = async () => {
    setDeletedLoading(true);
    setDeletedError('');
    try {
        const response = await fetch(`${API_URL}/offers/deleted`, { headers: getAuthHeaders() });
        const list = await readJsonOrThrow(response, 'Silinen teklifler alınamadı.');
        incrementReads(1);
        setDeletedOffers(Array.isArray(list) ? list : []);
    } catch (error) {
        console.error("Deleted offers fetch error", error);
        setDeletedOffers([]);
        setDeletedError(error.message);
    } finally {
        setDeletedLoading(false);
    }
  };

  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
        const response = await fetch(`${API_URL}/users`, {
            headers: getAuthHeaders()
        });
        if (response.ok) {
            const data = await response.json();
            incrementReads(1);
            setUsers(Array.isArray(data) ? data : []);
        } else {
            setUsers([]);
        }
    } catch (error) {
        console.error("Kullanıcılar çekilemedi:", error);
        setUsers([]);
    } finally {
        setLoadingUsers(false);
    }
  };

  const fetchLogs = async () => {
    setLoadingLogs(true);
    try {
      const params = new URLSearchParams();
      if (logStartDate) params.append('startDate', logStartDate);
      if (logEndDate) params.append('endDate', logEndDate);
      if (logSearchUser) params.append('userEmail', logSearchUser);

      const response = await fetch(`${API_URL}/activityLogs?${params.toString()}`, {
          headers: getAuthHeaders()
      });
      if (response.ok) {
          const logsData = await response.json();
          incrementReads(1);
          setLogs(Array.isArray(logsData) ? logsData : []);
      } else {
          setLogs([]);
      }
    } catch (error) {
      console.error("Loglar çekilemedi:", error);
      setLogs([]);
    } finally {
      setLoadingLogs(false);
    }
  };

  const handleAddSource = async (e) => {
    if (!canWrite) return alert("Yetkiniz yok.");
    e.preventDefault();
    if (!newSource.trim()) return;

    try {
      const response = await fetch(`${API_URL}/offerSources`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            name: newSource.trim(),
            createdBy: { uid: currentUser.uid, email: currentUser.email },
            createdAt: new Date()
          })
      });
      await readJsonOrThrow(response, 'Kaynak eklenemedi.');

      incrementWrites(2); // Kaynak + Log
      await writeLog('Kaynak Eklendi', `"${newSource.trim()}" kaynağı eklendi.`);

      setNewSource('');
      refreshData('offerSources');
    } catch (error) {
      console.error("Ekleme hatası:", error);
      alert("Kaynak eklenemedi: " + error.message);
    }
  };

  const handleDeleteSource = async (id) => {
    if (!canDelete) return alert("Yetkiniz yok.");
    if (window.confirm('Bu kaynağı silmek istediğinize emin misiniz?')) {
      try {
        const sourceName = sources.find(s => s.id === id)?.name;
        const response = await fetch(`${API_URL}/offerSources/${id}`, {
            method: 'DELETE',
            headers: getAuthHeaders()
        });
        await readJsonOrThrow(response, 'Kaynak silinemedi.');
        incrementWrites(2); // Silme + Log
        await writeLog('Kaynak Silindi', sourceName ? `"${sourceName}" kaynağı silindi (ID: ${id}).` : `Kaynak ID: ${id} silindi.`);
        refreshData('offerSources');
      } catch (error) {
        console.error("Silme hatası:", error);
        alert("Kaynak silinemedi: " + error.message);
      }
    }
  };

  const handleFileChange = (e, key) => {
    const file = e.target.files[0];
    if (file) {
        if (file.size > 700 * 1024) { // 700KB limit
            alert("Dosya boyutu çok büyük! Lütfen 700KB'dan küçük bir resim seçin.");
            return;
        }
        const reader = new FileReader();
        reader.onloadend = () => {
            setLogoSettings(prev => ({ ...prev, [key]: reader.result }));
        };
        reader.readAsDataURL(file);
    }
  };

  // KULLANICI YÖNETİMİ FONKSİYONLARI
  const handleUserFormChange = (field, value) => {
    setUserFormData(prev => ({ ...prev, [field]: value }));
  };

  const handlePermissionChange = (perm) => {
    setUserFormData(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [perm]: !prev.permissions[perm]
      }
    }));
  };

  const handleSaveUser = async (e) => {
    if (!isAdmin) return alert("Kullanıcı yönetimi sadece yönetici tarafından yapılabilir.");
    e.preventDefault();
    if (!userFormData.name || !userFormData.email) return;
    if (!editingUserId && !userFormData.password) {
      alert("Yeni kullanıcı için şifre gereklidir.");
      return;
    }

    try {
      if (editingUserId) {
        // Mevcut kullanıcıyı güncelle (Şifre güncelleme işlemi admin panelinden yapılmaz, sadece bilgiler)
        let { password, ...dataToUpdate } = userFormData;

        // Yönetici hesabı koruması: Yetkilerin değiştirilmesini engelle
        const userToEdit = users.find(u => u.id === editingUserId);
        if (userToEdit?.email === SUPER_ADMIN_EMAIL) {
            dataToUpdate.permissions = {
                read: true,
                write: true,
                delete: true,
                view: true,
                view_reports: true,
                view_financials: true,
                manage_users: true
            };
        }

        const response = await fetch(`${API_URL}/users/${editingUserId}`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            body: JSON.stringify(dataToUpdate)
        });
        if (!response.ok) throw new Error('Kullanıcı güncellenemedi.');
        setEditingUserId(null);
        incrementWrites(1); // Güncelleme işlemi için yazma sayısını artır
      } else {
        // Yeni kullanıcı oluşturma işlemi
        const response = await fetch(`${API_URL}/users`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({
              name: userFormData.name,
              email: userFormData.email,
              password: userFormData.password,
              permissions: userFormData.permissions,
              createdAt: new Date()
            })
        });
        if (!response.ok) throw new Error('Kullanıcı veritabanına kaydedilemedi.');
        
          incrementWrites(2); // Kullanıcı + Log
          await fetch(`${API_URL}/activityLogs`, {
              method: 'POST',
              headers: getAuthHeaders(),
              body: JSON.stringify({
                action: 'Kullanıcı Oluşturuldu',
                description: `${userFormData.email} kullanıcısı oluşturuldu.`,
                performedBy: { uid: currentUser.uid, email: currentUser.email }
              })
          });
      }
      
      setUserFormData({
        name: '',
        email: '',
        password: '',
        permissions: { read: true, write: false, delete: false, view: true, view_reports: false, view_financials: false, manage_users: false }
      });
      fetchUsers(); // Listeyi yenile
    } catch (error) {
      console.error("Kullanıcı kaydetme hatası:", error);
      alert("İşlem sırasında bir hata oluştu: " + error.message);
    }
  };

  const startEditingUser = (user) => {
    setEditingUserId(user.id);
    setUserFormData({
      name: user.name,
      email: user.email,
      password: '', // Düzenlerken şifre gösterilmez
      permissions: { read: true, write: false, delete: false, view: true, view_reports: false, view_financials: false, manage_users: false, ...user.permissions }
    });
  };

  const cancelEditingUser = () => {
    setEditingUserId(null);
    setUserFormData({
      name: '',
      email: '',
      password: '',
      permissions: { read: true, write: false, delete: false, view: true, view_reports: false, view_financials: false, manage_users: false }
    });
  };

  const handleDeleteUser = async (id) => {
    if (!isAdmin) return alert("Kullanıcı silme işlemi sadece yönetici tarafından yapılabilir.");
    
    const userToDelete = users.find(u => u.id === id);
    if (userToDelete?.email === SUPER_ADMIN_EMAIL) {
        return alert("Ana yönetici hesabı silinemez.");
    }

    if (window.confirm('Bu kullanıcıyı silmek istediğinize emin misiniz?')) {
      try {
        await fetch(`${API_URL}/activityLogs`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({
                action: 'Kullanıcı Silindi',
                description: `Kullanıcı ID: ${id} silindi.`,
                performedBy: { uid: currentUser.uid, email: currentUser.email }
            })
        });
        incrementWrites(2); // Silme + Log
        await fetch(`${API_URL}/users/${id}`, { 
            method: 'DELETE',
            headers: getAuthHeaders()
        });
        fetchUsers(); // Listeyi yenile
      } catch (error) {
        console.error("Silme hatası:", error);
      }
    }
  };

  const handleSaveLogos = async (e) => {
    e.preventDefault();
    if (!canWrite) return alert("Yetkiniz yok.");
    if (!logosLoaded) return alert("Kayıtlı ayarlar henüz okunamadı; üzerine yazılmaması için kaydetme durduruldu. Sayfayı yenileyip tekrar deneyin.");
    try {
        const response = await fetch(`${API_URL}/settings/logos`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({
                ...logoSettings,
                updatedBy: { uid: currentUser.uid, email: currentUser.email },
                updatedAt: new Date()
            })
        });
        await readJsonOrThrow(response, 'Logo ayarları kaydedilemedi.');

        incrementWrites(2); // Ayar + Log
        await writeLog('Logo Ayarları Güncellendi', 'Logo ve görünüm ayarları güncellendi.');
        
        // Apply changes immediately
        if (logoSettings.pageTitle) document.title = logoSettings.pageTitle;
        if (logoSettings.favicon) {
            let link = document.querySelector("link[rel~='icon']");
            if (!link) {
                link = document.createElement('link');
                link.rel = 'icon';
                document.getElementsByTagName('head')[0].appendChild(link);
            }
            link.href = logoSettings.favicon;
        }

        alert('Ayarlar başarıyla kaydedildi!');
    } catch (error) {
        console.error("Logo save error", error);
        alert("Hata oluştu: " + error.message);
    }
  };

  // Aktif sezon (settings kategorisi 'general'). Yeni teklifler bu sezona yazılır; kayıtlı teklifler etkilenmez.
  const handleSaveSeason = async (e) => {
    e.preventDefault();
    if (!canWrite) return alert("Yetkiniz yok.");
    if (!isValidSeason(seasonForm)) return alert("Geçersiz sezon.");
    // activeSeasonSaved false ise gösterilen sezon tarihten türetilmiştir → aynı değer de kaydedilebilmeli
    if (activeSeasonSaved && seasonForm === activeSeason) return alert("Bu sezon zaten kayıtlı.");
    if (!window.confirm(`Bundan sonra oluşturulan teklifler "${seasonForm}" sezonuna kaydedilecek. Onaylıyor musunuz?\n\nKayıtlı tekliflerin sezonu değişmez.`)) return;
    setSeasonSaving(true);
    try {
      const response = await fetch(`${API_URL}/settings/general`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          activeSeason: seasonForm,
          updatedBy: { uid: currentUser.uid, email: currentUser.email },
          updatedAt: new Date()
        })
      });
      await readJsonOrThrow(response, 'Sezon kaydedilemedi.');
      incrementWrites(2); // Ayar + Log
      await writeLog('Aktif Sezon Değiştirildi', `Aktif sezon "${activeSeason || '—'}" → "${seasonForm}" olarak değiştirildi.`);
      await refreshActiveSeason();
      alert(`Aktif sezon "${seasonForm}" olarak kaydedildi.`);
    } catch (error) {
      console.error("Season save error", error);
      alert("Hata oluştu: " + error.message);
    } finally {
      setSeasonSaving(false);
    }
  };

  const handleSaveBankSettings = async (e) => {
    if (!canWrite) return alert("Yetkiniz yok.");
    e.preventDefault();
    if (!bankLoaded) return alert("Kayıtlı banka ayarları henüz okunamadı; üzerine yazılmaması için kaydetme durduruldu. Sayfayı yenileyip tekrar deneyin.");
    try {
        // bankSettings sunucudaki kaydın tamamını taşır (bilinmeyen anahtarlar + e-posta şablonu korunur)
        const response = await fetch(`${API_URL}/settings/bank`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({
                ...bankSettings,
                updatedBy: { uid: currentUser.uid, email: currentUser.email },
                updatedAt: new Date()
            })
        });
        await readJsonOrThrow(response, 'Banka ayarları kaydedilemedi.');
        incrementWrites(2); // Ayar + Log
        await writeLog('Banka Ayarları Güncellendi', 'Banka hesap bilgileri güncellendi.');
        alert('Banka bilgileri başarıyla kaydedildi!');
    } catch (error) {
        console.error("Bank save error", error);
        alert("Hata oluştu: " + error.message);
    }
  };

  const handleSaveEmailSettings = async (e) => {
    if (!canWrite) return alert("Yetkiniz yok.");
    e.preventDefault();
    if (!bankLoaded) return alert("Kayıtlı ayarlar henüz okunamadı; banka bilgilerinin üzerine yazılmaması için kaydetme durduruldu. Sayfayı yenileyip tekrar deneyin.");
    try {
        const response = await fetch(`${API_URL}/settings/bank`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({
                ...bankSettings,
                updatedBy: { uid: currentUser.uid, email: currentUser.email },
                updatedAt: new Date()
            })
        });
        await readJsonOrThrow(response, 'E-posta ayarları kaydedilemedi.');
        incrementWrites(2); // Ayar + Log
        await writeLog('E-posta Ayarları Güncellendi', 'E-posta şablonu güncellendi.');
        alert('E-posta ayarları başarıyla kaydedildi!');
    } catch (error) {
        console.error("Email settings save error", error);
        alert("Hata oluştu: " + error.message);
    }
  };

  // Kendi şifresini değiştirme: sunucu mevcut şifreyi doğrular (POST /api/me/password)
  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!passwordData.currentPassword) {
      alert("Lütfen mevcut şifrenizi girin.");
      return;
    }
    if (passwordData.newPassword.length < 6) {
      alert("Yeni şifre en az 6 karakter olmalıdır.");
      return;
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      alert("Yeni şifreler eşleşmiyor!");
      return;
    }
    if (passwordData.newPassword === passwordData.currentPassword) {
      alert("Yeni şifre mevcut şifreyle aynı olamaz.");
      return;
    }

    setPasswordSaving(true);
    try {
      const response = await fetch(`${API_URL}/me/password`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            currentPassword: passwordData.currentPassword,
            newPassword: passwordData.newPassword
          })
      });
      const body = await readJsonOrThrow(response, 'Şifre güncellenemedi.');
      incrementWrites(1); // Şifre (işlem logunu sunucu yazar)
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      alert(body?.message || "Şifreniz başarıyla güncellendi.");
    } catch (error) {
      console.error("Şifre güncelleme hatası:", error);
      alert("Şifre değiştirilemedi: " + error.message);
    } finally {
      setPasswordSaving(false);
    }
  };

  // --- MESAJ ŞABLONLARI ---
  const handleMessageChange = (key, value) => {
    setMessageForm(prev => ({ ...prev, [key]: value }));
  };

  const handleMessageFocus = (key) => {
    setActiveMessageField(key);
    if (PREVIEW_KINDS.some(k => k.key === key)) setPreviewKind(key);
  };

  const resetMessageField = (key) => {
    setMessageForm(prev => ({ ...prev, [key]: defaultMessage(key) }));
  };

  // Yer tutucuyu son odaklanılan alana, imlecin olduğu yere ekler
  const insertPlaceholder = (token) => {
    if (!canWrite || messagesStatus !== 'ready') return;
    const key = activeMessageField;
    const el = messageInputRefs.current[key];
    const current = String(messageForm[key] ?? '');
    const start = el && typeof el.selectionStart === 'number' ? el.selectionStart : current.length;
    const end = el && typeof el.selectionEnd === 'number' ? el.selectionEnd : current.length;
    const next = current.slice(0, start) + token + current.slice(end);
    setMessageForm(prev => ({ ...prev, [key]: next }));
    requestAnimationFrame(() => {
      const target = messageInputRefs.current[key];
      if (target) {
        target.focus();
        const pos = start + token.length;
        try { target.setSelectionRange(pos, pos); } catch (err) { /* bazı input türleri desteklemez */ }
      }
    });
  };

  const handleSaveMessages = async (e) => {
    e.preventDefault();
    if (!canWrite) return alert("Yetkiniz yok.");
    if (messagesStatus !== 'ready') return alert("Kayıtlı şablonlar okunamadan kaydedilemez. Sayfayı yenileyip tekrar deneyin.");
    setMessagesSaving(true);
    try {
      const payload = {
        ...messageSaved, // ileride eklenecek bilinmeyen anahtarlar silinmesin
        ...messagePayloadFrom(messageForm),
        updatedBy: currentUser?.email || '',
        updatedAt: new Date().toISOString()
      };
      const response = await fetch(`${API_URL}/settings/messages`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
      });
      await readJsonOrThrow(response, 'Mesaj şablonları kaydedilemedi.');
      incrementWrites(2); // Ayar + Log
      setMessageSaved(payload);
      setMessageForm(messageFormFrom(payload));
      await writeLog('Mesaj Şablonları Güncellendi', 'WhatsApp / Instagram / fiyat listesi mesaj şablonları güncellendi.');
      alert('Mesaj şablonları kaydedildi.');
    } catch (error) {
      console.error("Message settings save error", error);
      alert("Kaydedilemedi: " + error.message);
    } finally {
      setMessagesSaving(false);
    }
  };

  // --- SİLİNENLER ---
  const handleRestoreOffer = async (offer) => {
    if (!canWrite) return alert("Yetkiniz yok.");
    setDeletedBusyId(offer.id);
    try {
      const response = await fetch(`${API_URL}/offers/${offer.id}/restore`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      await readJsonOrThrow(response, 'Teklif geri alınamadı.');
      incrementWrites(1); // log sunucuda
      await fetchDeletedOffers();
      refreshData('offers');
    } catch (error) {
      console.error("Restore error", error);
      alert("Geri alınamadı: " + error.message);
    } finally {
      setDeletedBusyId(null);
    }
  };

  const handlePermanentDelete = async (offer) => {
    if (!canDelete) return alert("Yetkiniz yok.");
    const name = offer.customerName || 'İsimsiz müşteri';
    if (!window.confirm(`"${name}" (#${offer.id}) teklifi KALICI olarak silinecek. Bu işlem geri alınamaz.\n\nDevam etmek istiyor musunuz?`)) return;
    setDeletedBusyId(offer.id);
    try {
      const response = await fetch(`${API_URL}/offers/${offer.id}/permanent`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      await readJsonOrThrow(response, 'Teklif kalıcı olarak silinemedi.');
      incrementWrites(1); // log sunucuda
      await fetchDeletedOffers();
      refreshData('offers');
    } catch (error) {
      console.error("Permanent delete error", error);
      alert("Kalıcı silinemedi: " + error.message);
    } finally {
      setDeletedBusyId(null);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  return (
    <div>
      <div className="max-w-4xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white">Ayarlar</h1>
        <button
            onClick={checkConnection}
            disabled={connectionStatus === 'checking'}
            className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors text-sm font-medium ${
                connectionStatus === 'success' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                connectionStatus === 'error' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
        >
            {connectionStatus === 'checking' ? (
                <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-current border-t-transparent"></div>
                    Kontrol Ediliyor...
                </>
            ) : connectionStatus === 'success' ? (
                <>
                    <CheckCircle size={18} />
                    Bağlantı Başarılı
                </>
            ) : connectionStatus === 'error' ? (
                <>
                    <AlertCircle size={18} />
                    Bağlantı Hatası
                </>
            ) : (
                <>
                    <Wifi size={18} />
                    Bağlantıyı Kontrol Et
                </>
            )}
        </button>
      </div>

      {/* Tab Navigation */}
      <div className="flex border-b border-gray-200 dark:border-gray-700 mb-6 bg-white dark:bg-gray-800 rounded-t-lg overflow-hidden shadow-sm overflow-x-auto">
        <button
            onClick={() => handleTabChange('sources')}
            className={`flex-1 py-4 px-6 text-center font-medium whitespace-nowrap transition-colors ${activeTab === 'sources' ? 'border-b-2 border-blue-600 text-blue-600 bg-blue-50 dark:bg-blue-900/20' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
        >
            Kaynaklar
        </button>
        <button
            onClick={() => handleTabChange('season')}
            className={`flex-1 py-4 px-6 text-center font-medium whitespace-nowrap transition-colors ${activeTab === 'season' ? 'border-b-2 border-blue-600 text-blue-600 bg-blue-50 dark:bg-blue-900/20' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
        >
            Sezon
        </button>
        <button
            onClick={() => handleTabChange('events')}
            className={`flex-1 py-4 px-6 text-center font-medium whitespace-nowrap transition-colors ${activeTab === 'events' ? 'border-b-2 border-blue-600 text-blue-600 bg-blue-50 dark:bg-blue-900/20' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
        >
            Organizasyon
        </button>
        <button
            onClick={() => handleTabChange('branding')}
            className={`flex-1 py-4 px-6 text-center font-medium whitespace-nowrap transition-colors ${activeTab === 'branding' ? 'border-b-2 border-blue-600 text-blue-600 bg-blue-50 dark:bg-blue-900/20' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
        >
            Logo & Görünüm
        </button>
        <button
            onClick={() => handleTabChange('bank')}
            className={`flex-1 py-4 px-6 text-center font-medium whitespace-nowrap transition-colors ${activeTab === 'bank' ? 'border-b-2 border-blue-600 text-blue-600 bg-blue-50 dark:bg-blue-900/20' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
        >
            Banka Hesapları
        </button>
        <button
            onClick={() => handleTabChange('email')}
            className={`flex-1 py-4 px-6 text-center font-medium whitespace-nowrap transition-colors ${activeTab === 'email' ? 'border-b-2 border-blue-600 text-blue-600 bg-blue-50 dark:bg-blue-900/20' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
        >
            E-posta Şablonu
        </button>
        <button
            onClick={() => handleTabChange('messages')}
            className={`flex-1 py-4 px-6 text-center font-medium whitespace-nowrap transition-colors ${activeTab === 'messages' ? 'border-b-2 border-blue-600 text-blue-600 bg-blue-50 dark:bg-blue-900/20' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
        >
            Mesaj Şablonları
        </button>
        <button
            onClick={() => handleTabChange('users')}
            className={`flex-1 py-4 px-6 text-center font-medium whitespace-nowrap transition-colors ${activeTab === 'users' ? 'border-b-2 border-blue-600 text-blue-600 bg-blue-50 dark:bg-blue-900/20' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
        >
            Kullanıcılar
        </button>
        <button
            onClick={() => handleTabChange('profile')}
            className={`flex-1 py-4 px-6 text-center font-medium whitespace-nowrap transition-colors ${activeTab === 'profile' ? 'border-b-2 border-blue-600 text-blue-600 bg-blue-50 dark:bg-blue-900/20' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
        >
            Profilim
        </button>
        <button
            onClick={() => handleTabChange('logs')}
            className={`flex-1 py-4 px-6 text-center font-medium whitespace-nowrap transition-colors ${activeTab === 'logs' ? 'border-b-2 border-blue-600 text-blue-600 bg-blue-50 dark:bg-blue-900/20' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
        >
            İşlem Geçmişi
        </button>
        <button
            onClick={() => handleTabChange('deleted')}
            className={`flex-1 py-4 px-6 text-center font-medium whitespace-nowrap transition-colors ${activeTab === 'deleted' ? 'border-b-2 border-blue-600 text-blue-600 bg-blue-50 dark:bg-blue-900/20' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
        >
            Silinenler
        </button>
      </div>

      {/* Organizasyon teklifleri: varsayılanlar, hazır kalemler, paketler, PDF koşulları (modules/events/EventSettings.js) */}
      {activeTab === 'events' && <EventSettings />}

      {/* Sezon: yeni tekliflerin yazılacağı aktif sezon. Kayıtlı tekliflerin sezonu değişmez. */}
      {activeTab === 'season' && (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-700 dark:text-white mb-4">Aktif Sezon</h2>
        <p className="text-gray-500 dark:text-gray-400 text-sm mb-6">
          Bundan sonra oluşturulan teklifler seçtiğiniz sezona kaydedilir. Kayıtlı tekliflerin sezonu değişmez.
          Ekranların üstündeki sezon seçicisiyle hangi sezonu görmek istediğinizi ayrıca seçebilirsiniz.
        </p>
        <form onSubmit={handleSaveSeason} className="space-y-4 max-w-md">
          <div>
            <label htmlFor="activeSeasonSelect" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Yeni teklifler bu sezona kaydedilsin</label>
            <select
              id="activeSeasonSelect"
              className="w-full p-2 border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              value={seasonForm}
              disabled={!canWrite}
              onChange={e => setSeasonForm(e.target.value)}
            >
              {seasonChoices.map(s => <option key={s} value={s}>{s} Sezonu</option>)}
            </select>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {activeSeasonSaved
              ? <>Şu an kayıtlı aktif sezon: <strong className="text-gray-700 dark:text-gray-200">{activeSeason}</strong></>
              : <>Kayıtlı bir sezon ayarı yok; şu an bugünün tarihinden türetiliyor: <strong className="text-gray-700 dark:text-gray-200">{activeSeason || '—'}</strong>. Kaydederek sabitleyebilirsiniz.</>}
          </p>
          {canWrite && (
            <button type="submit" disabled={seasonSaving} className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2 disabled:opacity-60">
              <Save size={18} /> {seasonSaving ? 'Kaydediliyor...' : (activeSeasonSaved ? 'Kaydet' : 'Bu sezonu sabitle')}
            </button>
          )}
        </form>
      </div>
      )}

      {activeTab === 'sources' && (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-700 dark:text-white mb-4">Teklif Kaynakları</h2>
        <p className="text-gray-500 dark:text-gray-400 text-sm mb-6">
          Rezervasyon teklifi oluştururken seçilecek kaynakları buradan yönetebilirsiniz (Örn: WhatsApp, Instagram, Telefon).
        </p>

        {canWrite && (
        <form onSubmit={handleAddSource} className="flex gap-4 mb-8">
          <input
            type="text"
            value={newSource}
            onChange={(e) => setNewSource(e.target.value)}
            placeholder="Yeni kaynak adı girin..."
            className="flex-1 p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          />
          <button
            type="submit"
            disabled={!newSource.trim()}
            className="bg-green-600 text-white px-6 py-2 rounded-lg flex items-center gap-2 hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus size={20} />
            Ekle
          </button>
        </form>
        )}

        {loadingSources && !data.offerSources ? (
          <LoadingSpinner />
        ) : (
          <div className="border dark:border-gray-700 rounded-lg overflow-hidden overflow-x-auto">
            {sources.length === 0 ? (
              <div className="p-4 text-center text-gray-500 bg-gray-50 dark:bg-gray-700 dark:text-gray-400">Henüz kaynak eklenmemiş.</div>
            ) : (
              <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                {sources.map((source) => (
                  <li key={source.id} className="p-4 flex justify-between items-center hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                    <span className="font-medium text-gray-700 dark:text-gray-200">{source.name}</span>
                    {canDelete && (
                    <button
                      onClick={() => handleDeleteSource(source.id)}
                      className="text-red-500 hover:text-red-700 p-2 rounded-full hover:bg-red-50 transition-colors"
                      title="Sil"
                    >
                      <Trash2 size={18} />
                    </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
      )}

      {/* İŞLEM GEÇMİŞİ (LOGLAR) BÖLÜMÜ */}
      {activeTab === 'logs' && (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="flex items-center gap-3 mb-4">
          <History className="text-gray-600 dark:text-gray-300" size={24} />
          <h2 className="text-lg font-semibold text-gray-700 dark:text-white">İşlem Geçmişi</h2>
        </div>
        <p className="text-gray-500 dark:text-gray-400 text-sm mb-6">
          Sistemde yapılan son işlemleri ve kullanıcı hareketlerini buradan takip edebilirsiniz.
        </p>

        {/* Filtreleme Alanı */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="relative flex-1">
                <Search className="absolute left-3 top-3 text-gray-400 dark:text-gray-500" size={20} />
                <input
                    type="text"
                    placeholder="Kullanıcı e-posta ile ara..."
                    className="w-full pl-10 p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
                    value={logSearchUser}
                    onChange={(e) => setLogSearchUser(e.target.value)}
                />
            </div>
            <div className="flex gap-2">
                <input 
                    type="date" 
                    className="p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    value={logStartDate}
                    onChange={(e) => setLogStartDate(e.target.value)}
                />
                <input 
                    type="date" 
                    className="p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    value={logEndDate}
                    onChange={(e) => setLogEndDate(e.target.value)}
                />
                <button onClick={fetchLogs} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2">
                    <Filter size={20} /> <span className="hidden sm:inline">Filtrele</span>
                </button>
            </div>
        </div>

        {loadingLogs ? (
            <LoadingSpinner />
        ) : (
            <>
            {/* Mobil Görünüm (Kartlar) */}
            <div className="md:hidden space-y-3">
                {logs.map((log) => (
                    <div key={log.id} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-md transition-shadow duration-200">
                        <div 
                            className="p-3 flex justify-between items-start cursor-pointer"
                            onClick={() => setExpandedLog(expandedLog === log.id ? null : log.id)}
                        >
                            <div>
                                <p className="font-medium text-sm text-gray-900 dark:text-white">{log.action}</p>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                    {log.timestamp ? new Date(log.timestamp).toLocaleString('tr-TR') : '-'}
                                </p>
                            </div>
                            {expandedLog === log.id ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
                        </div>
                        {expandedLog === log.id && (
                            <div className="px-3 pb-3 pt-0 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                                <div className="mt-2 text-xs text-gray-600 dark:text-gray-300 space-y-1">
                                    <p><span className="font-semibold">Detay:</span> {log.description}</p>
                                    <p><span className="font-semibold">Kullanıcı:</span> {log.performedBy?.email}</p>
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* Masaüstü Görünüm (Tablo) */}
            <div className="hidden md:block border dark:border-gray-700 rounded-lg overflow-hidden overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">İşlem</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Detay</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Kullanıcı</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Tarih</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {logs.map((log) => (
                            <tr key={log.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">{log.action}</td>
                                <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">{log.description}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{log.performedBy?.email}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-500 dark:text-gray-400">
                                    {log.timestamp ? new Date(log.timestamp).toLocaleString('tr-TR') : '-'}
                                </td>
                            </tr>
                        ))}
                        {logs.length === 0 && (
                            <tr>
                                <td colSpan="4" className="px-6 py-4 text-center text-gray-500">Henüz kayıt bulunmuyor.</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            </>
        )}
      </div>
      )}

      {/* LOGO AYARLARI BÖLÜMÜ */}
      {activeTab === 'branding' && (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="flex items-center gap-3 mb-4">
          <Image className="text-orange-600" size={24} />
          <h2 className="text-lg font-semibold text-gray-700 dark:text-white">Marka ve Logo Ayarları</h2>
        </div>
        <p className="text-gray-500 dark:text-gray-400 text-sm mb-6">
          Uygulamanın logosunu, favicon'unu ve sayfa başlığını buradan yönetebilirsiniz.
        </p>
        <form onSubmit={handleSaveLogos} className="space-y-4">
            <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Sayfa Başlığı</label>
                <input 
                    type="text" 
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    placeholder="Örn: Ramada Rezervasyon Paneli"
                    value={logoSettings.pageTitle}
                    onChange={(e) => setLogoSettings({...logoSettings, pageTitle: e.target.value})}
                />
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Odalar Linki (PDF Butonu)</label>
                <input 
                    type="text" 
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    placeholder="https://ramadaergan.com/villa-oda"
                    value={logoSettings.roomsLink || ''}
                    onChange={(e) => setLogoSettings({...logoSettings, roomsLink: e.target.value})}
                />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Otel Adı (PDF)</label>
                    <input 
                        type="text" 
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        placeholder="Ramada by Wyndham Ergan..."
                        value={logoSettings.hotelName || ''}
                        onChange={(e) => setLogoSettings({...logoSettings, hotelName: e.target.value})}
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Otel Telefon (PDF)</label>
                    <input 
                        type="text" 
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        placeholder="0547 811 24 24"
                        value={logoSettings.hotelPhone || ''}
                        onChange={(e) => setLogoSettings({...logoSettings, hotelPhone: e.target.value})}
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Otel E-Posta (PDF)</label>
                    <input 
                        type="text" 
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        placeholder="info@ramadaergan.com"
                        value={logoSettings.hotelEmail || ''}
                        onChange={(e) => setLogoSettings({...logoSettings, hotelEmail: e.target.value})}
                    />
                </div>
            </div>
            <div className="flex items-center gap-2 py-2">
                <input 
                    type="checkbox" 
                    id="showStats"
                    checked={logoSettings.showStats || false}
                    onChange={(e) => setLogoSettings({...logoSettings, showStats: e.target.checked})}
                    className="rounded text-blue-600 focus:ring-blue-500 w-4 h-4"
                />
                <label htmlFor="showStats" className="text-sm font-medium text-gray-700 dark:text-gray-300">Veritabanı İstatistiklerini Göster (Okuma/Yazma)</label>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Aydınlık Mod Logosu</label>
                    {logoSettings.light && <img src={logoSettings.light} alt="Light Logo" className="h-12 mb-2 object-contain" />}
                    <input type="file" accept="image/*" className="text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 dark:file:bg-blue-900/40 dark:file:text-blue-300 dark:hover:file:bg-blue-900/60" onChange={(e) => handleFileChange(e, 'light')} />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Karanlık Mod Logosu</label>
                    {logoSettings.dark && <img src={logoSettings.dark} alt="Dark Logo" className="h-12 mb-2 object-contain bg-gray-800 p-1 rounded" />}
                    <input type="file" accept="image/*" className="text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 dark:file:bg-blue-900/40 dark:file:text-blue-300 dark:hover:file:bg-blue-900/60" onChange={(e) => handleFileChange(e, 'dark')} />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Favicon</label>
                    {logoSettings.favicon && <img src={logoSettings.favicon} alt="Favicon" className="h-8 w-8 mb-2 object-contain" />}
                    <input type="file" accept="image/*" className="text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 dark:file:bg-blue-900/40 dark:file:text-blue-300 dark:hover:file:bg-blue-900/60" onChange={(e) => handleFileChange(e, 'favicon')} />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">PDF Arka Planı</label>
                    {logoSettings.pdfBackground && <div className="mb-2 text-xs text-green-600">Arka plan yüklü</div>}
                    <input type="file" accept="image/jpeg, image/png" className="text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 dark:file:bg-blue-900/40 dark:file:text-blue-300 dark:hover:file:bg-blue-900/60" onChange={(e) => handleFileChange(e, 'pdfBackground')} />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Giriş Ekranı Görseli</label>
                    {logoSettings.loginImage && <img src={logoSettings.loginImage} alt="Login Bg" className="h-24 mb-2 object-cover rounded" />}
                    <input type="file" accept="image/*" className="text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 dark:file:bg-blue-900/40 dark:file:text-blue-300 dark:hover:file:bg-blue-900/60" onChange={(e) => handleFileChange(e, 'loginImage')} />
                </div>
            </div>
            <div>
                <div className="flex justify-between items-center mb-1">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Otel Konsepti (HTML)</label>
                    <button 
                        type="button"
                        onClick={() => setShowHtmlSource(!showHtmlSource)}
                        className="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 flex items-center gap-1"
                    >
                        {showHtmlSource ? <><Eye size={14} /> Önizleme</> : <><Code size={14} /> HTML Kaynağı</>}
                    </button>
                </div>
                
                {showHtmlSource ? (
                    <textarea 
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 h-64 font-mono text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        value={logoSettings.hotelConcept || ''}
                        onChange={(e) => setLogoSettings({...logoSettings, hotelConcept: e.target.value})}
                        placeholder="HTML kodlarınızı buraya yapıştırabilirsiniz..."
                    />
                ) : (
                    <div className="bg-white dark:bg-gray-700 rounded-md border border-gray-300 dark:border-gray-600">
                        <ReactQuill 
                            theme="snow"
                            value={logoSettings.hotelConcept || ''}
                            onChange={(value) => setLogoSettings({...logoSettings, hotelConcept: value})}
                            className="h-64 pb-12 dark:text-white"
                            modules={{
                                toolbar: [
                                            [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
                                            ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                                            [{'list': 'ordered'}, {'list': 'bullet'}, {'indent': '-1'}, {'indent': '+1'}],
                                            [{ 'color': [] }, { 'background': [] }],
                                            [{ 'align': [] }],
                                            ['link', 'image'],
                                            ['clean']
                                ],
                            }}
                        />
                    </div>
                )}
            </div>
            {canWrite && (
            <div className="flex justify-end">
                <button 
                    type="submit" 
                    className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                >
                    Kaydet
                    <Save size={18} />
                </button>
            </div>
            )}
        </form>
      </div>
      )}

      {/* BANKA AYARLARI BÖLÜMÜ */}
      {activeTab === 'bank' && (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="flex items-center gap-3 mb-4">
          <CreditCard className="text-green-600" size={24} />
          <h2 className="text-lg font-semibold text-gray-700 dark:text-white">Banka Hesap Bilgileri</h2>
        </div>
        <p className="text-gray-500 dark:text-gray-400 text-sm mb-6">
          Teklif PDF'lerinde görünecek banka hesap bilgilerini buradan yönetebilirsiniz.
        </p>
        {bankError && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/30 text-sm text-red-700 dark:text-red-300 flex items-start gap-2">
            <AlertCircle size={18} className="shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="break-words">{bankError} Kayıtlı bilgilerin üzerine yazılmaması için kaydetme kapalı.</p>
              <button type="button" onClick={fetchBankSettings} className="mt-1 font-medium underline">Tekrar dene</button>
            </div>
          </div>
        )}
        <form onSubmit={handleSaveBankSettings} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Banka Adı</label>
                    <input 
                        type="text" 
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        placeholder="Örn: Ziraat Bankası"
                        value={bankSettings.bankName}
                        onChange={(e) => setBankSettings({...bankSettings, bankName: e.target.value})}
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Hesap Sahibi</label>
                    <input 
                        type="text" 
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        placeholder="Örn: Ramada Otelcilik A.Ş."
                        value={bankSettings.accountHolder}
                        onChange={(e) => setBankSettings({...bankSettings, accountHolder: e.target.value})}
                    />
                </div>
                <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">TL IBAN</label>
                    <input 
                        type="text" 
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 font-mono dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        placeholder="TR..."
                        value={bankSettings.tlIban}
                        onChange={(e) => setBankSettings({...bankSettings, tlIban: e.target.value})}
                    />
                </div>
                <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">EURO IBAN</label>
                    <input 
                        type="text" 
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 font-mono dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        placeholder="TR..."
                        value={bankSettings.euroIban}
                        onChange={(e) => setBankSettings({...bankSettings, euroIban: e.target.value})}
                    />
                </div>
                <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">USD IBAN</label>
                    <input 
                        type="text" 
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 font-mono dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        placeholder="TR..."
                        value={bankSettings.usdIban || ''}
                        onChange={(e) => setBankSettings({...bankSettings, usdIban: e.target.value})}
                    />
                </div>
                <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">İptal ve İade Koşulları</label>
                    <textarea 
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 h-24 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        placeholder="İptal ve iade şartlarını buraya giriniz..."
                        value={bankSettings.cancellationPolicy || ''}
                        onChange={(e) => setBankSettings({...bankSettings, cancellationPolicy: e.target.value})}
                    />
                </div>
                <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Mail Order Bilgilendirme</label>
                    <textarea 
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 h-24 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        placeholder="Mail order ile ilgili bilgilendirme metni..."
                        value={bankSettings.mailOrderInfo || ''}
                        onChange={(e) => setBankSettings({...bankSettings, mailOrderInfo: e.target.value})}
                    />
                </div>
            </div>
            {canWrite && (
            <div className="flex justify-end">
                <button 
                    type="submit" 
                    className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                >
                    Kaydet
                    <Save size={18} />
                </button>
            </div>
            )}
        </form>
      </div>
      )}

      {/* E-POSTA ŞABLONU BÖLÜMÜ */}
      {activeTab === 'email' && (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="flex items-center gap-3 mb-4">
          <Mail className="text-blue-600" size={24} />
          <h2 className="text-lg font-semibold text-gray-700 dark:text-white">E-posta Şablonu</h2>
        </div>
        <p className="text-gray-500 dark:text-gray-400 text-sm mb-6">
            Müşterilere gönderilecek e-postaların konu ve içeriğini buradan yönetebilirsiniz.
        </p>
        {bankError && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/30 text-sm text-red-700 dark:text-red-300 flex items-start gap-2">
            <AlertCircle size={18} className="shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="break-words">{bankError} Kayıtlı bilgilerin üzerine yazılmaması için kaydetme kapalı.</p>
              <button type="button" onClick={fetchBankSettings} className="mt-1 font-medium underline">Tekrar dene</button>
            </div>
          </div>
        )}
        <form onSubmit={handleSaveEmailSettings} className="space-y-4">
            <div className="grid grid-cols-1 gap-6">
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">E-posta Konusu</label>
                    <input 
                        type="text" 
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        placeholder="Ramada by Wyndham Ergan - Rezervasyon Teklifi"
                        value={bankSettings.emailSubject || ''}
                        onChange={(e) => setBankSettings({...bankSettings, emailSubject: e.target.value})}
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">E-posta İçeriği</label>
                    <textarea 
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 h-32 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        placeholder="Sayın {customerName}, ..."
                        value={bankSettings.emailBody || ''}
                        onChange={(e) => setBankSettings({...bankSettings, emailBody: e.target.value})}
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Kullanabileceğiniz değişkenler: <strong>{'{customerName}'}</strong> (Müşteri Adı), <strong>{'{link}'}</strong> (Teklif Linki), <strong>{'{totalPrice}'}</strong> (Tutar), <strong>{'{checkIn}'}</strong> (Giriş), <strong>{'{checkOut}'}</strong> (Çıkış), <strong>{'{nights}'}</strong> (Gece), <strong>{'{stay}'}</strong> (Tarih ve gece, ör. 25-28 Aralık 2026 (3 gece)), <strong>{'{rooms}'}</strong> (Oda tipleri), <strong>{'{offerNo}'}</strong> (Teklif No), <strong>{'{hotelPhone}'}</strong> (Otel Telefonu)
                    </p>
                </div>
            </div>
            {canWrite && (
            <div className="flex justify-end">
                <button 
                    type="submit" 
                    className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                >
                    Kaydet
                    <Save size={18} />
                </button>
            </div>
            )}
        </form>
      </div>
      )}

      {/* MESAJ ŞABLONLARI (settings 'messages'): WhatsApp / Instagram / fiyat listesi. Boş = varsayılan metin. */}
      {activeTab === 'messages' && (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6">
        <div className="flex items-center gap-3 mb-4">
          <MessageSquare className="text-green-600 shrink-0" size={24} />
          <h2 className="text-lg font-semibold text-gray-700 dark:text-white">Mesaj Şablonları</h2>
        </div>
        <p className="text-gray-500 dark:text-gray-400 text-sm mb-6">
          Teklif paylaşırken müşteriye giden WhatsApp, Instagram ve fiyat listesi mesajları. Tarih, tutar ve link
          tekliften otomatik doldurulur. Varsayılan metne dönen alan kod varsayılanını kullanmaya devam eder.
        </p>

        {!canWrite && (
          <div className="mb-4 p-3 rounded-lg bg-gray-50 dark:bg-gray-700 text-sm text-gray-600 dark:text-gray-300">
            Yazma yetkiniz olmadığı için şablonları yalnızca görüntüleyebilirsiniz.
          </div>
        )}

        {(messagesStatus === 'idle' || messagesStatus === 'loading') && <LoadingSpinner />}

        {messagesStatus === 'error' && (
          <div className="p-4 rounded-lg bg-red-50 dark:bg-red-900/30 text-sm text-red-700 dark:text-red-300 flex items-start gap-2">
            <AlertCircle size={18} className="shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="break-words">{messagesError}</p>
              <button type="button" onClick={fetchMessages} className="mt-2 font-medium underline">Tekrar dene</button>
            </div>
          </div>
        )}

        {messagesStatus === 'ready' && (() => {
          const previewText = buildOfferMessage(previewKind, SAMPLE_OFFER, {
            messages: messageForm,
            logos: logoSettings,
            origin: typeof window !== 'undefined' ? window.location.origin : ''
          });
          const activeFieldLabel = activeMessageField === 'priceListLink'
            ? 'Fiyat listesi linki'
            : (MESSAGE_FIELDS.find(f => f.key === activeMessageField)?.label || '');
          const savedBy = typeof messageSaved.updatedBy === 'object' ? messageSaved.updatedBy?.email : messageSaved.updatedBy;
          const roomsLink = String(logoSettings.roomsLink || '').trim();
          return (
          <form onSubmit={handleSaveMessages} className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            <div className="lg:col-span-3 space-y-5 min-w-0">
              {MESSAGE_FIELDS.map((field) => {
                const isDefault = normalizeText(messageForm[field.key]) === normalizeText(defaultMessage(field.key));
                const isActive = activeMessageField === field.key;
                return (
                  <div key={field.key}>
                    <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 mb-1">
                      <label htmlFor={`msg-${field.key}`} className="text-sm font-medium text-gray-700 dark:text-gray-300">{field.label}</label>
                      <div className="flex items-center gap-3">
                        {isDefault
                          ? <span className="text-xs text-gray-400 dark:text-gray-500">Varsayılan metin</span>
                          : <span className="text-xs text-amber-600 dark:text-amber-400">Özelleştirildi</span>}
                        {canWrite && !isDefault && (
                          <button
                            type="button"
                            onClick={() => resetMessageField(field.key)}
                            className="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 flex items-center gap-1"
                          >
                            <RotateCcw size={12} /> Varsayılana dön
                          </button>
                        )}
                      </div>
                    </div>
                    <textarea
                      id={`msg-${field.key}`}
                      ref={(el) => { messageInputRefs.current[field.key] = el; }}
                      rows={field.rows}
                      readOnly={!canWrite}
                      value={messageForm[field.key]}
                      onChange={(e) => handleMessageChange(field.key, e.target.value)}
                      onFocus={() => handleMessageFocus(field.key)}
                      className={`w-full p-2 border rounded-md text-sm leading-relaxed focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white ${isActive && canWrite ? 'border-blue-400 dark:border-blue-500' : 'border-gray-300 dark:border-gray-600'} ${!canWrite ? 'bg-gray-50 dark:bg-gray-700/60' : ''}`}
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{field.hint}</p>
                  </div>
                );
              })}

              <div>
                <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 mb-1">
                  <label htmlFor="msg-priceListLink" className="text-sm font-medium text-gray-700 dark:text-gray-300">Fiyat listesi linki</label>
                  {canWrite && String(messageForm.priceListLink || '').trim() && (
                    <button
                      type="button"
                      onClick={() => resetMessageField('priceListLink')}
                      className="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 flex items-center gap-1"
                    >
                      <RotateCcw size={12} /> Varsayılana dön
                    </button>
                  )}
                </div>
                <input
                  id="msg-priceListLink"
                  type="text"
                  inputMode="url"
                  ref={(el) => { messageInputRefs.current.priceListLink = el; }}
                  readOnly={!canWrite}
                  value={messageForm.priceListLink || ''}
                  onChange={(e) => handleMessageChange('priceListLink', e.target.value)}
                  onFocus={() => handleMessageFocus('priceListLink')}
                  placeholder={roomsLink || DEFAULT_PRICE_LIST_LINK}
                  className={`w-full p-2 border rounded-md text-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white ${activeMessageField === 'priceListLink' && canWrite ? 'border-blue-400 dark:border-blue-500' : 'border-gray-300 dark:border-gray-600'} ${!canWrite ? 'bg-gray-50 dark:bg-gray-700/60' : ''}`}
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 break-words">
                  {'{priceListLink}'} yerine gelir. Boş bırakılırsa Logo &amp; Görünüm'deki "Odalar Linki" kullanılır
                  {roomsLink ? <> (<span className="break-all">{roomsLink}</span>)</> : ' (tanımlı değil; varsayılan link kullanılır)'}.
                </p>
              </div>
            </div>

            <div className="lg:col-span-2 min-w-0">
              <div className="lg:sticky lg:top-4 space-y-4">
                {/* Canlı önizleme */}
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                  <div className="px-3 py-2 bg-gray-50 dark:bg-gray-700 flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-200">
                    <Eye size={16} /> Canlı önizleme
                  </div>
                  <div className="p-3 flex flex-wrap gap-1 border-b border-gray-200 dark:border-gray-700">
                    {PREVIEW_KINDS.map((k) => (
                      <button
                        key={k.key}
                        type="button"
                        onClick={() => setPreviewKind(k.key)}
                        className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${previewKind === k.key ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'}`}
                      >
                        {k.label}
                      </button>
                    ))}
                  </div>
                  <div className="p-3 bg-gray-100 dark:bg-gray-900">
                    <div
                      className="ml-auto max-w-full rounded-lg rounded-tr-none px-3 py-2 text-sm text-gray-800 dark:text-gray-100 bg-green-50 border border-green-100 dark:bg-green-900/30 dark:border-green-800 shadow-sm whitespace-pre-wrap break-words [overflow-wrap:anywhere]"
                    >
                      {previewText || <span className="text-gray-400">(boş mesaj)</span>}
                    </div>
                  </div>
                  <p className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">
                    Örnek teklif: Ayşe Yılmaz · Deluxe Oda · 25-28 Aralık 2026 · 85.000 TL
                  </p>
                </div>

                {/* Yer tutucu yardımı */}
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-200">Yer tutucular</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 break-words">
                    {canWrite
                      ? <>Tıklayınca <strong>{activeFieldLabel}</strong> alanında imlecin olduğu yere eklenir.</>
                      : 'Mesajda süslü parantezli alanlar tekliften doldurulur.'}
                  </p>
                  <ul className="mt-2 space-y-1.5">
                    {TEMPLATE_PLACEHOLDERS.map(([token, desc]) => (
                      <li key={token} className="flex items-start gap-2 text-xs">
                        <button
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => insertPlaceholder(token)}
                          disabled={!canWrite}
                          className="shrink-0 font-mono px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300 dark:hover:bg-blue-900/50 disabled:hover:bg-blue-50 disabled:cursor-default"
                          title={canWrite ? 'Alana ekle' : undefined}
                        >
                          {token}
                        </button>
                        <span className="text-gray-500 dark:text-gray-400 min-w-0 break-words pt-0.5">{desc}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            <div className="lg:col-span-5 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3">
              <p className="text-xs text-gray-400 dark:text-gray-500 break-words">
                {messageSaved.updatedAt
                  ? <>Son güncelleme: {formatDateTime(messageSaved.updatedAt)}{savedBy ? ` · ${savedBy}` : ''}</>
                  : 'Henüz kaydedilmiş özel şablon yok; varsayılan metinler kullanılıyor.'}
              </p>
              {canWrite && (
                <button
                  type="submit"
                  disabled={messagesSaving}
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {messagesSaving ? 'Kaydediliyor...' : 'Kaydet'}
                  <Save size={18} />
                </button>
              )}
            </div>
          </form>
          );
        })()}
      </div>
      )}

      {/* ŞİFRE DEĞİŞTİRME BÖLÜMÜ */}
      {activeTab === 'profile' && (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="flex items-center gap-3 mb-4">
          <Lock className="text-purple-600" size={24} />
          <h2 className="text-lg font-semibold text-gray-700 dark:text-white">Şifre Değiştir</h2>
        </div>
        <p className="text-gray-500 dark:text-gray-400 text-sm mb-6">
          Mevcut hesabınızın şifresini buradan değiştirebilirsiniz. Yeni şifre en az 6 karakter olmalıdır.
        </p>
        <form onSubmit={handleChangePassword} className="space-y-4">
            <input type="text" name="username" autoComplete="username" value={currentUser?.email || ''} readOnly hidden />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                    <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Mevcut Şifre</label>
                    <input
                        id="currentPassword"
                        type="password"
                        autoComplete="current-password"
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        value={passwordData.currentPassword}
                        onChange={(e) => setPasswordForm({...passwordData, currentPassword: e.target.value})}
                        placeholder="******"
                    />
                </div>
                <div>
                    <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Yeni Şifre</label>
                    <input
                        id="newPassword"
                        type="password"
                        autoComplete="new-password"
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        value={passwordData.newPassword}
                        onChange={(e) => setPasswordForm({...passwordData, newPassword: e.target.value})}
                        placeholder="******"
                    />
                </div>
                <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Yeni Şifre (Tekrar)</label>
                    <input
                        id="confirmPassword"
                        type="password"
                        autoComplete="new-password"
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        value={passwordData.confirmPassword}
                        onChange={(e) => setPasswordForm({...passwordData, confirmPassword: e.target.value})}
                        placeholder="******"
                    />
                </div>
            </div>
            {passwordData.confirmPassword && passwordData.newPassword !== passwordData.confirmPassword && (
                <p className="text-xs text-red-600 dark:text-red-400">Yeni şifreler eşleşmiyor.</p>
            )}
            <div className="flex justify-end">
                <button
                    type="submit"
                    disabled={passwordSaving}
                    className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition-colors h-10 w-full md:w-auto disabled:opacity-60"
                >
                    {passwordSaving ? 'Güncelleniyor...' : 'Şifreyi Güncelle'}
                </button>
            </div>
        </form>
      </div>
      )}

      {/* KULLANICI YÖNETİMİ BÖLÜMÜ */}
      {activeTab === 'users' && (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="flex items-center gap-3 mb-4">
          <User className="text-blue-600" size={24} />
          <h2 className="text-lg font-semibold text-gray-700 dark:text-white">Kullanıcı Yönetimi</h2>
        </div>
        <p className="text-gray-500 dark:text-gray-400 text-sm mb-6">
          Sisteme erişimi olan kullanıcıları ve yetkilerini buradan yönetebilirsiniz.
        </p>

        {/* Kullanıcı Ekleme/Düzenleme Formu */}
        {isAdmin && (
        <form onSubmit={handleSaveUser} className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg mb-6 border border-gray-200 dark:border-gray-600">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <input
              type="text"
              placeholder="Ad Soyad"
              className="p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-600 dark:border-gray-500 dark:text-white"
              value={userFormData.name}
              onChange={(e) => handleUserFormChange('name', e.target.value)}
              required
            />
            <input
              type="email"
              placeholder="E-posta Adresi"
              className="p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-600 dark:border-gray-500 dark:text-white"
              value={userFormData.email}
              onChange={(e) => handleUserFormChange('email', e.target.value.trim())}
              required
            />
            {!editingUserId && (
              <input
                type="password"
                placeholder="Şifre"
                className="p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-600 dark:border-gray-500 dark:text-white"
                value={userFormData.password}
                onChange={(e) => handleUserFormChange('password', e.target.value)}
                required
              />
            )}
          </div>
          
          <div className="mb-4">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-2">Yetkiler:</span>
            <div className="flex flex-wrap gap-4">
              {Object.keys(userFormData.permissions).map((perm) => (
                <label key={perm} className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={userFormData.permissions[perm]}
                    onChange={() => handlePermissionChange(perm)}
                    className="rounded text-blue-600 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={editingUserId && users.find(u => u.id === editingUserId)?.email === 'abdullah@amcfikirsanat.com'}
                    disabled={editingUserId && users.find(u => u.id === editingUserId)?.email === SUPER_ADMIN_EMAIL}
                  />
                  <span className="text-sm text-gray-600 dark:text-gray-300 capitalize">
                    {perm === 'read' ? 'Okuma' : 
                     perm === 'write' ? 'Yazma' : 
                     perm === 'delete' ? 'Silme' : 
                     perm === 'view_reports' ? 'Raporlar' :
                     perm === 'view_financials' ? 'Ciro/Finans' :
                     perm === 'manage_users' ? 'Kullanıcı Ynt.' :
                     'Görüntüleme'}
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-2">
            {editingUserId && (
              <button
                type="button"
                onClick={cancelEditingUser}
                className="px-4 py-2 text-gray-600 hover:bg-gray-200 rounded-lg flex items-center gap-1 transition-colors"
              >
                <X size={18} /> İptal
              </button>
            )}
            <button
              type="submit"
              className="bg-blue-600 text-white px-6 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700 transition-colors"
            >
              {editingUserId ? <Save size={18} /> : <Plus size={18} />}
              {editingUserId ? 'Güncelle' : 'Kullanıcı Ekle'}
            </button>
          </div>
        </form>
        )}

        {/* Kullanıcı Listesi */}
        {loadingUsers ? (
          <LoadingSpinner />
        ) : (
          <>
          {/* Mobil Görünüm (Kartlar) */}
          <div className="md:hidden space-y-3">
            {users.map((user) => (
                <div key={user.id} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 hover:shadow-md transition-shadow duration-200">
                    <div className="flex justify-between items-start mb-2">
                        <div>
                            <h3 className="font-medium text-gray-900 dark:text-white">{user.name}</h3>
                            <p className="text-xs text-gray-500 dark:text-gray-400">{user.email}</p>
                        </div>
                        <div className="flex gap-2">
                            {isAdmin && <button onClick={() => startEditingUser(user)} className="text-blue-600 hover:bg-blue-50 p-1 rounded"><Pencil size={16} /></button>}
                            {isAdmin && <button onClick={() => handleDeleteUser(user.id)} className="text-red-600 hover:bg-red-50 p-1 rounded"><Trash2 size={16} /></button>}
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-1 mb-2">
                        {Object.entries(user.permissions || {}).map(([key, val]) => (
                          val && <span key={key} className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 capitalize">
                            {key === 'read' ? 'Okuma' : 
                             key === 'write' ? 'Yazma' : 
                             key === 'delete' ? 'Silme' : 
                             key === 'view_reports' ? 'Raporlar' :
                             key === 'view_financials' ? 'Finans' :
                             key === 'manage_users' ? 'Admin' :
                             'Görüntüleme'}
                          </span>
                        ))}
                    </div>
                    <div className="text-[10px] text-gray-400 border-t border-gray-100 dark:border-gray-700 pt-2 mt-2 flex flex-wrap gap-x-3">
                        <span>Kayıt: {formatDateOnly(user.createdAt)}</span>
                        <span>Son giriş: {formatDateTime(user.lastLogin)}</span>
                    </div>
                </div>
            ))}
          </div>

          {/* Masaüstü Görünüm (Tablo) */}
          <div className="hidden md:block border dark:border-gray-700 rounded-lg overflow-hidden overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Kullanıcı</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Yetkiler</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Tarihçe</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">İşlemler</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {users.map((user) => (
                  <tr key={user.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">{user.name}</div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">{user.email}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {Object.entries(user.permissions || {}).map(([key, val]) => (
                          val && <span key={key} className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800 capitalize">
                            {key === 'read' ? 'Okuma' : 
                             key === 'write' ? 'Yazma' : 
                             key === 'delete' ? 'Silme' : 
                             key === 'view_reports' ? 'Raporlar' :
                             key === 'view_financials' ? 'Finans' :
                             key === 'manage_users' ? 'Admin' :
                             'Görüntüleme'}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        <div className="flex flex-col">
                            <span className="text-xs">Kayıt: {formatDateOnly(user.createdAt)}</span>
                            <span className={`text-xs ${user.lastLogin ? 'text-blue-600 dark:text-blue-400' : ''}`}>Son giriş: {formatDateTime(user.lastLogin)}</span>
                        </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {isAdmin && <button onClick={() => startEditingUser(user)} className="text-blue-600 hover:text-blue-900 mr-3" title="Düzenle">
                        <Pencil size={18} />
                      </button>}
                      {isAdmin && <button onClick={() => handleDeleteUser(user.id)} className="text-red-600 hover:text-red-900" title="Sil">
                        <Trash2 size={18} />
                      </button>}
                    </td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr>
                    <td colSpan="4" className="px-6 py-4 text-center text-gray-500">Henüz kullanıcı eklenmemiş.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          </>
        )}
      </div>
      )}

      {/* SİLİNENLER: yumuşak silinmiş teklifler (geri al / kalıcı sil). Loglar sunucuda yazılır. */}
      {activeTab === 'deleted' && (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-3 min-w-0">
            <Archive className="text-red-600 shrink-0" size={24} />
            <h2 className="text-lg font-semibold text-gray-700 dark:text-white">Silinen Teklifler</h2>
          </div>
          {canWrite && (
            <button
              type="button"
              onClick={fetchDeletedOffers}
              disabled={deletedLoading}
              className="px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-2 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-60"
              title="Listeyi yenile"
            >
              <RefreshCw size={16} className={deletedLoading ? 'animate-spin' : ''} />
              <span className="hidden sm:inline">Yenile</span>
            </button>
          )}
        </div>
        <p className="text-gray-500 dark:text-gray-400 text-sm mb-6">
          Silinen teklifler burada saklanır; geri alınana ya da kalıcı silinene kadar listelerde, takvimde ve raporlarda görünmez.
        </p>

        {!canWrite ? (
          <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-700 text-sm text-gray-600 dark:text-gray-300">
            Silinen teklifleri görmek için yazma yetkisi gerekir.
          </div>
        ) : deletedLoading && deletedOffers.length === 0 && !deletedError ? (
          <LoadingSpinner />
        ) : deletedError ? (
          <div className="p-4 rounded-lg bg-red-50 dark:bg-red-900/30 text-sm text-red-700 dark:text-red-300 flex items-start gap-2">
            <AlertCircle size={18} className="shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="break-words">{deletedError}</p>
              <button type="button" onClick={fetchDeletedOffers} className="mt-2 font-medium underline">Tekrar dene</button>
            </div>
          </div>
        ) : deletedOffers.length === 0 ? (
          <div className="p-4 text-center text-gray-500 bg-gray-50 dark:bg-gray-700 dark:text-gray-400 rounded-lg">Silinen teklif yok.</div>
        ) : (
          <>
          {/* Mobil Görünüm (Kartlar) */}
          <div className="md:hidden space-y-3">
            {deletedOffers.map((offer) => {
              const busy = deletedBusyId === offer.id;
              const deletedBy = typeof offer.deletedBy === 'object' ? offer.deletedBy?.email : offer.deletedBy;
              return (
                <div key={offer.id} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                  <div className="flex justify-between items-start gap-2 mb-2">
                    <div className="min-w-0">
                      <h3 className="font-medium text-gray-900 dark:text-white break-words">{offer.customerName || '—'}</h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">#{offer.id}{offer.phone ? ` · ${offer.phone}` : ''}</p>
                    </div>
                    {canSeeMoney && (
                      <span className="text-sm font-semibold text-gray-700 dark:text-gray-200 whitespace-nowrap">{offerMoneyText(offer)}</span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300">{formatStay(offer) || '—'}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 break-words">
                    Silindi: {formatDateTime(offer.deletedAt)}{deletedBy ? ` · ${deletedBy}` : ''}
                  </p>
                  <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                    <button
                      type="button"
                      onClick={() => handleRestoreOffer(offer)}
                      disabled={busy}
                      className="flex-1 px-3 py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-1 bg-green-50 text-green-700 hover:bg-green-100 dark:bg-green-900/30 dark:text-green-300 dark:hover:bg-green-900/50 disabled:opacity-60"
                    >
                      <RotateCcw size={16} /> Geri al
                    </button>
                    {canDelete && (
                      <button
                        type="button"
                        onClick={() => handlePermanentDelete(offer)}
                        disabled={busy}
                        className="flex-1 px-3 py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-1 bg-red-50 text-red-700 hover:bg-red-100 dark:bg-red-900/30 dark:text-red-300 dark:hover:bg-red-900/50 disabled:opacity-60"
                      >
                        <Trash2 size={16} /> Kalıcı sil
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Masaüstü Görünüm (Tablo) */}
          <div className="hidden md:block border dark:border-gray-700 rounded-lg overflow-hidden overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Müşteri</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Telefon</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Konaklama</th>
                  {canSeeMoney && <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Tutar</th>}
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Silinme / Silen</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">İşlemler</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {deletedOffers.map((offer) => {
                  const busy = deletedBusyId === offer.id;
                  const deletedBy = typeof offer.deletedBy === 'object' ? offer.deletedBy?.email : offer.deletedBy;
                  return (
                    <tr key={offer.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">{offer.customerName || '—'}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">#{offer.id}</div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">{offer.phone || '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{formatStay(offer) || '—'}</td>
                      {canSeeMoney && (
                        <td className="px-4 py-3 text-sm text-right font-medium text-gray-700 dark:text-gray-200 whitespace-nowrap">{offerMoneyText(offer)}</td>
                      )}
                      <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                        <div className="whitespace-nowrap">{formatDateTime(offer.deletedAt)}</div>
                        {deletedBy && <div className="text-xs break-all">{deletedBy}</div>}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => handleRestoreOffer(offer)}
                            disabled={busy}
                            className="px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1 whitespace-nowrap bg-green-50 text-green-700 hover:bg-green-100 dark:bg-green-900/30 dark:text-green-300 dark:hover:bg-green-900/50 disabled:opacity-60"
                            title="Teklifi listelere geri getir"
                          >
                            <RotateCcw size={14} /> Geri al
                          </button>
                          {canDelete && (
                            <button
                              type="button"
                              onClick={() => handlePermanentDelete(offer)}
                              disabled={busy}
                              className="px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1 whitespace-nowrap bg-red-50 text-red-700 hover:bg-red-100 dark:bg-red-900/30 dark:text-red-300 dark:hover:bg-red-900/50 disabled:opacity-60"
                              title="Teklifi kalıcı olarak sil (geri alınamaz)"
                            >
                              <Trash2 size={14} /> Kalıcı sil
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          </>
        )}
      </div>
      )}
      </div>
    </div>
  );
}
